import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { usersApi, type ImportRow, type StudentImport } from '@/api/users';
import { MAdd, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { IS_APP_BUILD } from '@/lib/appBrand';

/**
 * Импорты — что и когда загружали, и где взять пароли.
 *
 * 🔴 ЗАЧЕМ ОТДЕЛЬНЫЙ РАЗДЕЛ. 05.09.2026 заказчик загрузил 348 человек: браузер
 * бросил ждать на 30-й секунде, и таблица с паролями просто не дошла — а других
 * мест, где её взять, не было. Теперь отчёт и пароли живут на сервере: забрать
 * можно когда угодно, хоть на следующий день, хоть с другого компьютера.
 */
export function AdminImportsPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'imports'],
    queryFn: () => usersApi.listImports(30),
    // ⚠️ Пока хоть одна загрузка идёт — переспрашиваем. Иначе человек смотрит на
    // «идёт» и не понимает, закончилось оно или зависло.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((x) => x.status === 'queued' || x.status === 'running') ? 3000 : false,
  });

  const items = list.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      {isMobile ? (
        // Телефон (§5.8): шапка набора, «Импорт файлом» — круглой «+» (только admin, у manager был бы 403).
        <MTitle
          title={t('imports.title')}
          subtitle={t('imports.subtitle')}
          right={role === 'admin' ? <MAdd label={t('adminStudentImport.open')} onClick={() => navigate('/admin/imports/new')} /> : undefined}
        />
      ) : (
      <PageHeader
        title={t('imports.title')}
        subtitle={t('imports.subtitle')}
        actions={
          // Загрузка файлом — только admin (manager получил бы 403).
          role === 'admin' && (
            <Link
              to="/admin/imports/new"
              className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-on-primary"
            >
              <span className="material-symbols-outlined text-[18px]">upload_file</span>
              {t('adminStudentImport.open')}
            </Link>
          )
        }
      />
      )}

      {items.length === 0 && !list.isLoading && (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('imports.empty')}
        </Card>
      )}

      {items.map((imp) => (
        <ImportCard
          key={imp.id}
          imp={imp}
          open={open === imp.id}
          onToggle={() => setOpen(open === imp.id ? null : imp.id)}
        />
      ))}
    </div>
  );
}

function ImportCard({ imp, open, onToggle }: { imp: StudentImport; open: boolean; onToggle: () => void }) {
  const { t, i18n } = useTranslation();
  const detail = useQuery({
    queryKey: ['admin', 'imports', imp.id],
    queryFn: () => usersApi.getImport(imp.id),
    enabled: open,
  });

  const running = imp.status === 'queued' || imp.status === 'running';
  const [saving, setSaving] = useState(false);

  /**
   * ⚠️ ФАЙЛ ЗАБИРАЕТСЯ ЗАПРОСОМ, А НЕ ССЫЛКОЙ. Пропуск мы передаём заголовком, а
   * обычная ссылка заголовков не шлёт — сервер ответил бы «войдите», и человек
   * решил бы, что кнопка сломана.
   */
  const download = async () => {
    setSaving(true);
    try {
      const blob = await usersApi.downloadImportPasswords(imp.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onebilim-dostupy-${imp.created_at.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setSaving(false);
    }
  };
  const when = new Date(imp.created_at).toLocaleString(i18n.language, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* У загрузки файлом тарифа нет — называем её файлом, иначе в журнале одни «без тарифа». */}
          {/* В приложении ни слова «тариф» (решение владельца 13.10.2026): название тарифа — данные с сервера, его не
              показываем, только имя файла или «Список учеников». */}
          <CardTitle>
            {IS_APP_BUILD
              ? (imp.source === 'file' && imp.file_name) || t('imports.listUpload')
              : imp.tariff_title || (imp.source === 'file' && imp.file_name) || t('imports.noTariff')}
          </CardTitle>
          <p className="mt-0.5 text-sm text-on-surface-variant">
            {when} · {t('imports.rows', { count: imp.total })}
          </p>
        </div>
        <StatusPill status={imp.status} />
      </div>

      {/* ⚠️ Показываем ВСЕ числа, а не только «завели». Разница между «завели 300»
          и «зачислили 300» — это и есть та беда, из-за которой 348 человек
          остались без тарифа. Скрыть её значит повторить. */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Num label={t('imports.created')} value={imp.created} accent />
        {!IS_APP_BUILD && <Num label={t('imports.enrolled')} value={imp.enrolled} accent={imp.enrolled > 0} />}
        <Num label={t('imports.existing')} value={imp.existing} />
        <Num label={t('imports.problems')} value={imp.invalid + imp.duplicate + imp.failed} />
      </div>

      {imp.error && (
        <p className="mt-3 rounded-card bg-error/5 p-3 text-sm text-error">{imp.error}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {imp.created > 0 && !running && (
          <Button loading={saving} onClick={download}>
            <span className="material-symbols-outlined text-[18px]">download</span>
            {t('imports.downloadPasswords')}
          </Button>
        )}
        <Button variant="ghost" onClick={onToggle}>
          {open ? t('imports.hideRows') : t('imports.showRows')}
        </Button>
      </div>

      {open && (
        // Своя прокрутка — только на компьютере: на телефоне вложенная прокрутка ловит палец.
        <div className="mt-3 rounded-card border border-outline-variant md:max-h-72 md:overflow-y-auto">
          {detail.isLoading ? (
            <p className="p-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {(detail.data?.rows ?? []).map((r) => <RowLine key={r.line} row={r} />)}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}

function RowLine({ row }: { row: ImportRow }) {
  const { t } = useTranslation();
  const tone: Record<ImportRow['state'], string> = {
    new: 'text-primary',
    existing: 'text-on-surface-variant',
    duplicate: 'text-on-surface-variant',
    invalid: 'text-error',
    failed: 'text-error',
  };
  return (
    <tr className="border-b border-outline-variant/60 last:border-0">
      <td className="w-10 p-2 text-right text-xs tabular-nums text-on-surface-variant">{row.line}</td>
      <td className="p-2">
        <span className="font-medium">{row.full_name}</span>
        <span className="ml-2 break-all text-xs text-on-surface-variant">{row.email}</span>
        {row.reason && <span className="ml-2 text-xs text-error">{row.reason}</span>}
      </td>
      <td className={`whitespace-nowrap p-2 text-right text-xs ${tone[row.state]}`}>
        {t(`imports.state_${row.state}`)}
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: StudentImport['status'] }) {
  const { t } = useTranslation();
  const variant =
    status === 'done' ? 'success' : status === 'failed' ? 'danger' : 'warning';
  return <Pill variant={variant}>{t(`imports.status_${status}`)}</Pill>;
}

function Num({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div
      className={`rounded-card border p-3 ${
        accent ? 'border-primary bg-primary/5' : 'border-outline-variant bg-surface-variant/30'
      }`}
    >
      <p className={`text-2xl font-bold tabular-nums ${accent ? 'text-primary' : ''}`}>{value}</p>
      <p className="text-xs text-on-surface-variant">{label}</p>
    </div>
  );
}
